/**
 * Candidate attribution TypeScript types (Phase 1 of candidate-attribution).
 *
 * Mirrors the Pydantic schemas in `backend/app/schemas/candidate.py`, which in
 * turn wrap the ORM models in `backend/app/models/candidate.py`,
 * `candidate_social.py`, and `candidate_region_scope.py`.
 *
 * Single source of truth is the backend. Any drift between these types and
 * the Pydantic schemas is a bug — fix backend first, mirror here second.
 * (Same rule as `src/types/canvass.ts`.)
 *
 * These live in their own module rather than `@/types/index.ts` on purpose:
 * the canvass side needs to import them without pulling the whole barrel.
 *
 * Shape naming mirrors the Python side exactly:
 *   CandidateCreate   → payload for POST /candidates
 *   CandidateUpdate   → payload for PATCH /candidates/{id}
 *   CandidateProfileUpdate
 *                     → payload for PATCH /candidates/{id}/profile, the
 *                       candidate's own narrow self-edit surface
 *   CandidateListItem → response row from GET /candidates
 *   CandidateRead     → response from GET /candidates/{id} (and POST/PATCH)
 *
 * ⚠️ **The list and the detail are NOT the same shape.** `GET /candidates`
 * returns `CandidateListItem`, which omits `bio`, `headshot_path`,
 * `website_url`, `donate_url`, `socials`, and `updated_at`. Only
 * `GET /candidates/{id}` returns the full `CandidateRead`. An edit form that
 * hydrates from a list row will silently blank those fields on save — fetch
 * the detail first.
 *
 * ⚠️ Neither shape carries `tenant_id`. The router forces the caller's tenant
 * and never serializes it back.
 *
 * Two rules from the model docstrings are load-bearing here:
 *
 *  1. `slug` is the stable identifier and must NEVER be repurposed — a slug
 *     is what historical knock leans resolve through, so reusing one for a
 *     different person silently re-attributes history. `CandidateUpdate`
 *     omits it by construction; the backend does not accept it either.
 *  2. There is no DELETE. Deactivation is hide-not-delete
 *     (`candidate.py` / `campaign_issue.py:59-61`): an inactive candidate
 *     leaves the picker but historical leans must still resolve to a name.
 */

// ---------------------------------------------------------------------------
// Enums (string unions)
// ---------------------------------------------------------------------------

/**
 * Mirrors the `SocialPlatform` Literal in `schemas/candidate.py` and the
 * `candidate_socials_platform_valid` CHECK. String + CHECK (not a PG enum) so
 * adding a platform never needs an enum migration — platforms churn faster
 * than anything else in this schema. A value accepted by the schema but
 * rejected by the CHECK is a 500, so these three lists must stay in lockstep.
 */
export type CandidateSocialPlatform =
  | 'x'
  | 'facebook'
  | 'instagram'
  | 'youtube'
  | 'tiktok'
  | 'truth'
  | 'rumble'
  | 'linkedin'
  | 'other';

export const CANDIDATE_SOCIAL_PLATFORMS: CandidateSocialPlatform[] = [
  'x',
  'facebook',
  'instagram',
  'youtube',
  'tiktok',
  'truth',
  'rumble',
  'linkedin',
  'other',
];

/** Human labels for the platform picker. */
export const CANDIDATE_SOCIAL_PLATFORM_LABELS: Record<CandidateSocialPlatform, string> = {
  x: 'X / Twitter',
  facebook: 'Facebook',
  instagram: 'Instagram',
  youtube: 'YouTube',
  tiktok: 'TikTok',
  truth: 'Truth Social',
  rumble: 'Rumble',
  linkedin: 'LinkedIn',
  other: 'Other',
};

/** Mirrors `schemas.user.ScopeKind`, reused verbatim by the candidate schema. */
export type CandidateScopeKind = 'ld' | 'subregion';

// ---------------------------------------------------------------------------
// Socials
// ---------------------------------------------------------------------------

/**
 * Write shape for one social row (`CandidateSocialIn`). `url` is required and
 * validated non-empty server-side; `handle` is optional and normalized to
 * null when blank.
 */
export interface CandidateSocialInput {
  platform: CandidateSocialPlatform;
  handle?: string | null;
  url: string;
  display_order?: number;
}

/**
 * Read shape (`CandidateSocialOut`). Note it carries NO `id`, `candidate_id`,
 * or `created_at` — the serializer deliberately returns only the four fields
 * the UI renders, so it round-trips directly into `CandidateSocialInput`.
 */
export interface CandidateSocialRead {
  platform: CandidateSocialPlatform;
  handle: string | null;
  url: string;
  display_order: number;
}

// ---------------------------------------------------------------------------
// Region scopes — the candidate's authoritative REACH
// ---------------------------------------------------------------------------

/**
 * Write shape for one reach row (`CandidateScopeIn`).
 *
 * ⚠️ The wire spelling is `{kind, ld, precincts}` — deliberately identical to
 * `UserScopeInput`, NOT the `{scope_kind, scope_ld, scope_precincts}` DB
 * column spelling. (Verified against a live 200 response, 2026-07-15: the
 * plan's §3.3 column list describes the table, not the payload.)
 *
 * Cross-field rules enforced server-side — violating either is a 422:
 *   - `kind: 'subregion'` REQUIRES a non-empty `precincts`.
 *   - `kind: 'ld'` FORBIDS `precincts` entirely. A stray list would be
 *     silently ignored at filter time, so it's rejected loudly instead.
 *   - `ld` must be a numeric district id ("12"); non-numeric blows up inside
 *     `_ld_like` at filter-build time.
 *
 * ⚠️ `precincts` is `string[]`, not `number[]`, because `Voter.precinct_num`
 * is a String column. The turf catalog carries `precinctsAsStrings` for
 * exactly this — use it on write rather than re-coercing a numeric list. The
 * schema has `mode="before"` validators that
 * would coerce ints, but do not lean on that: the sibling `UserScopeInput`
 * lacks them and 422s on int precincts, so the two payloads behave
 * differently for the same mistake.
 *
 * ⚠️ Zero scope rows on a CANDIDATE means "county-wide office" (Sheriff,
 * Assessor) — NOT "unscoped". It is the same predicate as a user's
 * zero-rows-means-tenant-global but a different fact, and conflating the two
 * when computing a linked user's effective scope is the escalation trap in
 * plan §5.1.
 */
export interface CandidateScopeInput {
  kind: CandidateScopeKind;
  ld: string;
  /** Required for 'subregion', forbidden for 'ld'. STRINGS on the wire. */
  precincts?: string[] | null;
}

/**
 * Read shape (`CandidateScopeOut`). `label` is precomputed server-side exactly
 * as `UserScopeOut` computes it ("LD 12" / "LD 12 — precincts 8, 35"), so
 * candidate reach and user turf can render through one component. Prefer it
 * over re-deriving a label client-side.
 */
export interface CandidateScopeRead {
  kind: CandidateScopeKind;
  ld: string;
  precincts: string[] | null;
  label: string;
}

// ---------------------------------------------------------------------------
// Candidate write bodies
// ---------------------------------------------------------------------------

/**
 * POST /candidates body. Gated {ADMIN, ORG_MANAGER}.
 *
 * `tenant_id` is absent by design — the router forces the caller's tenant and
 * never trusts a client-supplied one. `is_active` is absent too: a new
 * candidate is always created active.
 */
export interface CandidateCreate {
  /** Stable machine id, lowercase-kebab. Trimmed+lowercased server-side. */
  slug: string;
  full_name: string;
  /** Short script-merge form ("Smith"); falls back to full_name for display. */
  display_name?: string | null;
  office: string;
  /** Display ONLY ("LD 12"). The authoritative reach is `scopes`. */
  district_label?: string | null;
  party?: string | null;
  /** ISO date (YYYY-MM-DD). */
  election_date?: string | null;
  display_order?: number;
  bio?: string | null;
  website_url?: string | null;
  donate_url?: string | null;
  socials?: CandidateSocialInput[];
  /**
   * ADMIN-only server-side: a non-ADMIN caller sending a NON-EMPTY `scopes`
   * gets a 403 (an empty list is fine). Omit rather than send `[]` when the
   * caller can't set them.
   */
  scopes?: CandidateScopeInput[];
}

/**
 * PATCH /candidates/{id} body. Gated {ADMIN, ORG_MANAGER}.
 *
 * Every field optional. `socials` and `scopes` are **replace-all when present
 * — including when present as an empty list** — and untouched when absent
 * (the handler splits on `model_dump(exclude_unset=True)`). So sending
 * `scopes: []` wipes a candidate's reach; omit the key to leave it alone.
 *
 * `slug` is intentionally not updatable, here or server-side.
 */
export interface CandidateUpdate {
  full_name?: string;
  display_name?: string | null;
  office?: string;
  district_label?: string | null;
  party?: string | null;
  election_date?: string | null;
  is_active?: boolean;
  display_order?: number;
  bio?: string | null;
  website_url?: string | null;
  donate_url?: string | null;
  socials?: CandidateSocialInput[];
  scopes?: CandidateScopeInput[];
  /**
   * Human confirmation that an empty `scopes` really means county-wide.
   *
   * Editing a candidate's reach re-materializes the region scopes of every
   * account linked to them with `relation='candidate'`. Zero scopes means
   * TENANT-GLOBAL, not "no access", so an edit that empties the reach hands
   * that login the whole county — an escalation arriving through the door
   * marked "shrink". The server refuses it with a 409 unless this flag says a
   * human meant it. Only set it from an explicit confirmation; never default
   * it true to make a form submit.
   *
   * Ignored when the candidate has no linked login: with no account to
   * protect there is nothing to refuse.
   */
  county_wide_confirmed?: boolean;
}

/**
 * Payload for `PATCH /candidates/{id}/profile` — what a CANDIDATE may change
 * about themselves, and the only write on this entity they have.
 *
 * A separate interface rather than `Pick<CandidateUpdate, …>` on purpose: the
 * difference between the two is a privilege boundary, and a `Pick` invites
 * someone to widen it by adding one more key. Mirrors
 * `schemas/candidate.py::CandidateProfileUpdate`, which sets
 * `extra="forbid"` — so an extra key here is a **422, not a silent no-op**.
 * Sending `scopes`, `office`, `is_active`, `display_order`, `party`,
 * `district_label`, `election_date`, `full_name`, or `slug` through this
 * endpoint fails the request outright. Staff use `CandidateUpdate` for those.
 *
 * `socials` follows the same replace-all rule as `CandidateUpdate`: present
 * (even `[]`) replaces every row, `undefined` leaves them alone.
 *
 * The headshot is not here — it is multipart, with its own endpoints.
 */
export interface CandidateProfileUpdate {
  display_name?: string | null;
  bio?: string | null;
  website_url?: string | null;
  donate_url?: string | null;
  socials?: CandidateSocialInput[];
}

// ---------------------------------------------------------------------------
// Candidate responses
// ---------------------------------------------------------------------------

/**
 * One row of `GET /candidates` (`CandidateListItem`) — the admin table.
 *
 * Intentionally lighter than `CandidateRead`: no bio/headshot/urls/socials.
 * Don't hydrate an edit form from this — see the module header.
 */
export interface CandidateListItem {
  id: number;
  slug: string;
  full_name: string;
  display_name: string | null;
  office: string;
  district_label: string | null;
  party: string | null;
  election_date: string | null;
  is_active: boolean;
  display_order: number;
  scopes: CandidateScopeRead[];
  created_at: string;
}

/** The full record — `GET /candidates/{id}`, and the POST/PATCH/deactivate response. */
export interface CandidateRead {
  id: number;
  slug: string;
  full_name: string;
  display_name: string | null;
  office: string;
  district_label: string | null;
  party: string | null;
  election_date: string | null;
  is_active: boolean;
  display_order: number;
  bio: string | null;
  /** Server-relative path, not a URL. EXIF-stripped server-side on upload. */
  headshot_path: string | null;
  website_url: string | null;
  donate_url: string | null;
  socials: CandidateSocialRead[];
  scopes: CandidateScopeRead[];
  created_at: string;
  updated_at: string | null;
}

/**
 * The compact form (`CandidateBrief`) — the briefing slate chip (plan §8.2
 * tiers 1 & 3). Rides along with the shift payload and gets cached
 * client-side, because a chip that fetches on render is a blank chip on a
 * rural porch (plan §8.1.1). Note: no `headshot_path`.
 *
 * Phase 1 admin doesn't consume this; declared here so the canvass side has
 * one place to import it from.
 */
export interface CandidateBrief {
  id: number;
  slug: string;
  full_name: string;
  display_name: string | null;
  office: string;
  party: string | null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * The name a volunteer actually says at the door. The fallback lives in the
 * backend serializer too; mirrored here so a `CandidateBrief` built
 * client-side (offline queue) resolves identically.
 */
export function candidateShortName(
  c: Pick<CandidateRead, 'display_name' | 'full_name'>,
): string {
  return c.display_name?.trim() || c.full_name;
}
